import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import classNames from "./classNames";
import "./SearchableCombobox.css";

export type SearchableComboboxOption<TValue extends string = string> = {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
  /**
   * Optional secondary search key. Defaults to `value`.
   * For "code – name" pickers, set `value` to the code and `search`
   * to the code too; the label can carry the human friendly form.
   */
  search?: string;
};

export type SearchableComboboxLabels = {
  /** Visible placeholder on the trigger when no value is selected. */
  placeholder?: string;
  /** Placeholder for the search input inside the popup. */
  searchPlaceholder?: string;
  /** Text shown when no options match the current search. */
  noResults?: string;
  /** ARIA label for the trigger button. */
  triggerAriaLabel?: string;
  /** ARIA label for the search input. */
  searchAriaLabel?: string;
  /** ARIA label for the listbox. */
  listAriaLabel?: string;
  /** Label for the "clear selection" entry when `allowClear` is true. */
  clearLabel?: string;
};

export type SearchableComboboxProps<TValue extends string = string> = {
  value: TValue | null | undefined;
  options: ReadonlyArray<SearchableComboboxOption<TValue>>;
  onChange: (value: TValue | null) => void;
  /** Maximum number of options to render in the list. Default: 100. */
  maxOptions?: number;
  /** Allow clearing the selection (returns null). Default: false. */
  allowClear?: boolean;
  /** Disable the combobox. */
  disabled?: boolean;
  /** Auto-open the popup on mount and focus the search input. */
  openOnMount?: boolean;
  /** Called when the popup closes without selecting (Escape / click outside). */
  onCancel?: () => void;
  /** ClassName appended to the trigger button. */
  triggerClassName?: string;
  /** ClassName appended to the popup container. */
  popupClassName?: string;
  /** Render override for the trigger contents. */
  renderTrigger?: (option: SearchableComboboxOption<TValue> | undefined) => ReactNode;
  /** Render override for an option row. */
  renderOption?: (option: SearchableComboboxOption<TValue>, isActive: boolean) => ReactNode;
  /** Custom labels / placeholders. */
  labels?: SearchableComboboxLabels;
  /** Fixed popup width in pixels. Defaults to max(triggerWidth, 280). */
  popupWidth?: number;
  /** Maximum popup height in pixels. Default 360. */
  popupMaxHeight?: number;
  /** Optional id for the trigger button. */
  id?: string;
  /** ARIA describedby for the trigger button. */
  "aria-describedby"?: string;
  /** Disable rendering through a portal (useful for tests). Default: false. */
  disablePortal?: boolean;
};

export type SearchableComboboxHandle = {
  open: () => void;
  close: () => void;
  focus: () => void;
};

const DEFAULT_LABELS: Required<SearchableComboboxLabels> = {
  placeholder: "Select…",
  searchPlaceholder: "Search…",
  noResults: "No matches",
  triggerAriaLabel: "Choose value",
  searchAriaLabel: "Search",
  listAriaLabel: "Options",
  clearLabel: "Clear selection",
};

function scoreOption(option: SearchableComboboxOption<string>, query: string): number {
  if (!query) return 1;
  const q = query.toLocaleLowerCase();
  const search = (option.search ?? option.value).toLocaleLowerCase();
  const label = option.label.toLocaleLowerCase();
  if (search.startsWith(q)) return 100;
  if (search.includes(q)) return 50;
  if (label.startsWith(q)) return 30;
  if (label.includes(q)) return 15;
  return 0;
}

function SearchableComboboxInner<TValue extends string>(
  props: SearchableComboboxProps<TValue>,
  ref: React.Ref<SearchableComboboxHandle>,
) {
  const {
    value,
    options,
    onChange,
    maxOptions = 100,
    allowClear = false,
    disabled = false,
    openOnMount = false,
    onCancel,
    triggerClassName,
    popupClassName,
    renderTrigger,
    renderOption,
    labels,
    popupWidth,
    popupMaxHeight = 360,
    id,
    "aria-describedby": ariaDescribedBy,
    disablePortal = false,
  } = props;

  const resolvedLabels: Required<SearchableComboboxLabels> = { ...DEFAULT_LABELS, ...labels };
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const listId = `${triggerId}-list`;
  const optionId = (val: string) => `${triggerId}-opt-${encodeURIComponent(val)}`;

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const activeOptionRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState<boolean>(Boolean(openOnMount));
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [popupStyle, setPopupStyle] = useState<CSSProperties>({
    position: "fixed",
    top: -9999,
    left: -9999,
    zIndex: 1000,
  });

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? undefined,
    [options, value],
  );

  const ranked = useMemo(() => {
    const trimmed = query.trim();
    const arr = options.map((option) => ({
      option,
      score: scoreOption(option as SearchableComboboxOption<string>, trimmed),
    }));
    if (trimmed) {
      arr.sort(
        (a, b) => b.score - a.score || a.option.label.localeCompare(b.option.label),
      );
    } else {
      arr.sort((a, b) => a.option.label.localeCompare(b.option.label));
    }
    return arr
      .filter((entry) => (trimmed ? entry.score > 0 : true) && !entry.option.disabled)
      .slice(0, maxOptions)
      .map((entry) => entry.option);
  }, [options, query, maxOptions]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useLayoutEffect(() => {
    if (!open) return;
    activeOptionRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  const computePopupStyle = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const margin = 4;
    const desiredWidth = popupWidth ?? Math.max(rect.width, 280);
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const placeBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(
      popupMaxHeight,
      Math.max(180, placeBelow ? spaceBelow : spaceAbove),
    );
    const top = placeBelow ? rect.bottom + margin : rect.top - maxHeight - margin;
    const left = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - desiredWidth - 8),
    );
    setPopupStyle({
      position: "fixed",
      top,
      left,
      width: desiredWidth,
      maxHeight,
      zIndex: 1000,
    });
  }, [popupMaxHeight, popupWidth]);

  useLayoutEffect(() => {
    if (!open) return;
    computePopupStyle();
  }, [open, computePopupStyle]);

  useEffect(() => {
    if (!open) return;
    const handle = () => computePopupStyle();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
  }, [open, computePopupStyle]);

  useEffect(() => {
    if (!open) return;
    // Ignore outside-click events that happen during the same task tick that
    // opened the popup. Without this, the very click that caused the parent
    // to switch into edit-mode (and re-render us with openOnMount=true) would
    // also fire mousedown on document and instantly close us again.
    const openedAt = performance.now();
    const onPointerDown = (event: PointerEvent) => {
      if (performance.now() - openedAt < 50) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (popupRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
      onCancel?.();
    };
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (popupRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
      onCancel?.();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn, true);
    };
  }, [open, onCancel]);

  const openPopup = useCallback(() => {
    if (disabled) return;
    setQuery("");
    setOpen(true);
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [disabled]);

  const closePopup = useCallback(
    (cancelled: boolean) => {
      setOpen(false);
      if (cancelled) onCancel?.();
    },
    [onCancel],
  );

  useImperativeHandle(
    ref,
    () => ({
      open: openPopup,
      close: () => closePopup(true),
      focus: () => triggerRef.current?.focus(),
    }),
    [openPopup, closePopup],
  );

  useEffect(() => {
    if (!openOnMount) return;
    // Mirror initial-open into focus only; state already started as open so
    // the popup is part of the very first render (no race with grid-cell
    // remounts that may unmount us before a regular useEffect would fire).
    requestAnimationFrame(() => searchInputRef.current?.focus());
    // openOnMount only triggers this once on mount.
  }, [openOnMount]);

  const commit = useCallback(
    (option: SearchableComboboxOption<TValue> | null) => {
      onChange(option ? option.value : null);
      setOpen(false);
    },
    [onChange],
  );

  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, Math.max(0, ranked.length - 1)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(0, index - 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(Math.max(0, ranked.length - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const next = ranked[activeIndex];
      if (next) commit(next);
    } else if (event.key === "Escape") {
      event.preventDefault();
      closePopup(true);
      triggerRef.current?.focus();
    } else if (event.key === "Tab") {
      const next = ranked[activeIndex];
      if (next) {
        event.preventDefault();
        commit(next);
      }
    }
  };

  const onTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPopup();
    }
  };

  const activeOption = ranked[activeIndex];

  const popupContent = (
    <div
      ref={popupRef}
      className={classNames("sp-combobox", "sp-combobox-popup", popupClassName)}
      style={popupStyle}
      role="dialog"
      aria-label={resolvedLabels.listAriaLabel}
    >
      <div className="sp-combobox-popup__search">
        <input
          ref={searchInputRef}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={resolvedLabels.searchPlaceholder}
          aria-label={resolvedLabels.searchAriaLabel}
          aria-controls={listId}
          aria-autocomplete="list"
        />
      </div>
      {allowClear && !query ? (
        <button
          type="button"
          className="sp-combobox-popup__clear"
          onMouseDown={(event) => {
            event.preventDefault();
            commit(null);
          }}
        >
          {resolvedLabels.clearLabel}
        </button>
      ) : null}
      <div
        id={listId}
        role="listbox"
        aria-label={resolvedLabels.listAriaLabel}
        className="sp-combobox-popup__list"
      >
        {ranked.length === 0 ? (
          <div className="sp-combobox-popup__empty">{resolvedLabels.noResults}</div>
        ) : (
          ranked.map((option, index) => {
            const isActive = index === activeIndex;
            const isSelected = option.value === value;
            return (
              <div
                key={option.value}
                ref={isActive ? activeOptionRef : undefined}
                id={optionId(option.value)}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  commit(option);
                }}
                className={classNames(
                  "sp-combobox-popup__option",
                  isActive && "sp-combobox-popup__option--active",
                  isSelected && "sp-combobox-popup__option--selected",
                )}
              >
                {renderOption
                  ? renderOption(option, isActive)
                  : (
                    <>
                      <span className="sp-combobox-popup__option-label">{option.label}</span>
                      {option.description ? (
                        <span className="sp-combobox-popup__option-description">
                          {option.description}
                        </span>
                      ) : null}
                    </>
                  )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        id={triggerId}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={
          open && activeOption ? optionId(activeOption.value) : undefined
        }
        aria-label={resolvedLabels.triggerAriaLabel}
        aria-describedby={ariaDescribedBy}
        onClick={() => {
          if (open) {
            // Already open (e.g. via openOnMount). Focus the search input
            // instead of toggling closed so users can refine their query.
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
          } else {
            openPopup();
          }
        }}
        onKeyDown={onTriggerKeyDown}
        className={classNames("sp-combobox", "sp-combobox-trigger", triggerClassName)}
      >
        <span
          className={classNames(
            "sp-combobox-trigger__label",
            !selectedOption && "sp-combobox-trigger__placeholder",
          )}
        >
          {renderTrigger
            ? renderTrigger(selectedOption)
            : selectedOption
              ? selectedOption.label
              : resolvedLabels.placeholder}
        </span>
        <span aria-hidden className="sp-combobox-trigger__chevron">
          ▾
        </span>
      </button>

      {open && typeof document !== "undefined"
        ? disablePortal
          ? popupContent
          : createPortal(popupContent, document.body)
        : null}
    </>
  );
}

export const SearchableCombobox = forwardRef(SearchableComboboxInner) as <
  TValue extends string = string,
>(
  props: SearchableComboboxProps<TValue> & {
    ref?: React.Ref<SearchableComboboxHandle>;
  },
) => ReturnType<typeof SearchableComboboxInner>;
